import { GroupMember } from '../groups/groupService';

export interface MentionedUser {
  user_id: string;
  name: string;
  position: number;
}

/**
 * Detect all mentions in a text string
 * Returns array of mentioned users with their positions in the text
 *
 * @param text - The message text to search
 * @param groupMembers - List of group members to match against
 * @returns Array of mentioned users
 */
export const detectMentions = (text: string, groupMembers: GroupMember[]): MentionedUser[] => {
  const mentions: MentionedUser[] = [];
  const mentionRegex = /@(\w+)/g;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const mentionedName = match[1];
    const position = match.index;

    // Find matching member (case-insensitive)
    const member = groupMembers.find(m =>
      m.user.name.toLowerCase().replace(/\s+/g, '').includes(mentionedName.toLowerCase())
    );

    if (member) {
      mentions.push({
        user_id: member.user_id,
        name: member.user.name,
        position,
      });
    }
  }

  return mentions;
};

/**
 * Parse the current @ mention being typed
 * Detects if cursor is after an @ symbol and returns the partial username
 *
 * @param text - The current message text
 * @param cursorPosition - Current cursor position in text
 * @returns Object with isMentioning flag and partial username, or null
 */
export const parseMentionTrigger = (
  text: string,
  cursorPosition: number
): { isMentioning: boolean; partial: string; startPos: number } | null => {
  // Look backward from cursor to find @ symbol
  const textBeforeCursor = text.substring(0, cursorPosition);
  const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

  // No @ found, or @ is not the most recent non-whitespace character
  if (lastAtSymbol === -1) {
    return null;
  }

  // Check if there's whitespace between @ and cursor (means mention was completed)
  const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
  if (textAfterAt.includes(' ')) {
    return null;
  }

  // We're actively typing a mention
  return {
    isMentioning: true,
    partial: textAfterAt,
    startPos: lastAtSymbol,
  };
};

/**
 * Get mention suggestions based on partial input
 * Filters group members by partial name match
 *
 * @param partial - The partial username being typed (after @)
 * @param groupMembers - List of group members
 * @returns Filtered list of matching members
 */
export const getMentionSuggestions = (
  partial: string,
  groupMembers: GroupMember[]
): GroupMember[] => {
  if (!partial || partial.length === 0) {
    // Show all members if no partial input
    return groupMembers;
  }

  const lowerPartial = partial.toLowerCase();

  return groupMembers.filter(member => {
    const name = member.user.name.toLowerCase();
    const nameWithoutSpaces = name.replace(/\s+/g, '');

    // Match by:
    // 1. Name starts with partial
    // 2. Name contains partial
    // 3. Name without spaces starts with partial
    return (
      name.startsWith(lowerPartial) ||
      name.includes(lowerPartial) ||
      nameWithoutSpaces.startsWith(lowerPartial)
    );
  });
};

/**
 * Replace the @mention being typed with the selected user's formatted mention
 *
 * @param text - The current message text
 * @param startPos - Position where @ symbol starts
 * @param cursorPosition - Current cursor position
 * @param selectedMember - The member selected from autocomplete
 * @returns Object with new text and new cursor position
 */
export const insertMention = (
  text: string,
  startPos: number,
  cursorPosition: number,
  selectedMember: GroupMember
): { newText: string; newCursorPosition: number } => {
  // Format username: remove spaces and use camelCase or use as-is
  const formattedName = selectedMember.user.name.replace(/\s+/g, '');
  const mentionText = `@${formattedName} `;

  // Replace from @ to cursor with the formatted mention
  const before = text.substring(0, startPos);
  const after = text.substring(cursorPosition);

  const newText = before + mentionText + after;
  const newCursorPosition = startPos + mentionText.length;

  return { newText, newCursorPosition };
};

/**
 * Check if a specific user is mentioned in a message
 *
 * @param mentions - Array of mentioned users
 * @param userId - User ID to check
 * @returns true if user is mentioned
 */
export const isUserMentioned = (mentions: MentionedUser[], userId: string): boolean => {
  return mentions.some(mention => mention.user_id === userId);
};

/**
 * Extract user IDs from mentions array
 *
 * @param mentions - Array of mentioned users
 * @returns Array of user IDs
 */
export const getMentionedUserIds = (mentions: MentionedUser[]): string[] => {
  return mentions.map(mention => mention.user_id);
};
